import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { FiMenu, FiX, FiSun, FiMoon, FiGlobe } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toggleTheme } from '../../store/slices/themeSlice';
import { logoutUser } from '../../store/slices/authSlice';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { user } = useSelector((s) => s.auth);
  const { mode } = useSelector((s) => s.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/learn', label: t('nav.learn') },
    { to: '/tips', label: t('nav.tips') },
    { to: '/stories', label: t('nav.stories') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'mr' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('language', next);
  };

  const dashboardPath = user?.role === 'admin' ? '/admin' : '/dashboard';
  const dashboardLabel = user?.role === 'admin' ? t('dashboard.admin') : t('nav.dashboard');

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-950/80 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">💰</span>
            <span className="font-display font-bold text-lg gradient-text hidden sm:block">Paise Ki Pathshala</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={toggleLang} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" title={t('common.language')}>
              <FiGlobe className="w-5 h-5" />
              <span className="text-xs ml-1">{i18n.language.toUpperCase()}</span>
            </button>
            <button onClick={() => dispatch(toggleTheme())} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              {mode === 'dark' ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>

            {user ? (
              <>
                <Link to={dashboardPath} className="btn-primary text-sm py-2 px-4 hidden sm:inline-flex">{dashboardLabel}</Link>
                <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-red-500 hidden sm:block">{t('nav.logout')}</button>
              </>
            ) : (
              <Link to="/login" className="btn-primary text-sm py-2 px-4 hidden sm:inline-flex">{t('nav.login')}</Link>
            )}

            <button className="md:hidden p-2" onClick={() => setOpen(!open)}>
              {open ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950">
            <div className="px-4 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 font-medium">{l.label}</Link>
              ))}
              {user ? (
                <>
                  <Link to={dashboardPath} onClick={() => setOpen(false)} className="btn-primary text-center">{dashboardLabel}</Link>
                  <button onClick={handleLogout} className="text-red-500 py-2">{t('nav.logout')}</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="btn-primary text-center">{t('nav.login')}</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
